import { FC } from "react";
import { Link as RouterLink } from "react-router-dom";

import { Link } from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { ITicket, TicketSilo, TicketSiloFormattedValues } from "interfaces/entities/ticket";
import { AgentsRoutes } from "routes/agents/constants";

import ToolbarLabel from "./ToolbarLabel";

interface IProps {
  ticket: ITicket;
  isViewer: boolean;
  isAgent: boolean;
  canManageTickets: boolean;
  isMemberOfTicketTeam: boolean;
  onSiloChange: (silo: TicketSilo) => void;
  onDeleteTicket: () => void;
}

const TicketToolbarLabels: FC<IProps> = ({
  ticket,
  canManageTickets,
  isMemberOfTicketTeam,
  onSiloChange,
  onDeleteTicket,
  isViewer,
  isAgent,
}) => {
  const { silo, parentTicket } = ticket;
  const { shortID: parentShortId, ID: parentTicketId } = parentTicket || {};
  const disableSiloActions = isViewer || !canManageTickets || !isMemberOfTicketTeam;
  const shouldDisplayArchiveLabel = silo === TicketSilo.Archive;
  const shouldDisplayTrashLabel = silo === TicketSilo.Trash;
  const shouldDisplaySpamLabel = silo === TicketSilo.Spam;
  const shouldDisplayChildTicketLabel = ticket.parentTicket !== null;
  const shouldDisplayNotMemberOfTeamLabel = !isMemberOfTicketTeam && canManageTickets;

  return (
    <Flex column paddingH="4px">
      {isViewer ? (
        (shouldDisplayArchiveLabel || shouldDisplayTrashLabel || shouldDisplaySpamLabel) && (
          <ToolbarLabel
            title={
              <>
                {`This ticket is in ${TicketSiloFormattedValues[silo]}. To edit the ticket `}
                <RouterLink to={AgentsRoutes.Agents} style={{ textDecoration: "none" }}>
                  <Link bold={false}>contact any admin</Link>
                </RouterLink>
                &nbsp;to change your role.
              </>
            }
          />
        )
      ) : (
        <>
          {shouldDisplayArchiveLabel && (
            <ToolbarLabel
              title="This ticket is in Archive. You can't edit archived ticket."
              firstActionText="Unarchive"
              firstActionOnClick={() => onSiloChange(TicketSilo.Tickets)}
              secondActionText="Move to Trash"
              secondActionOnClick={() => onSiloChange(TicketSilo.Trash)}
              disableActions={disableSiloActions}
            />
          )}
          {shouldDisplayTrashLabel && (
            <ToolbarLabel
              title="This ticket is in Trash. You can't edit deleted ticket."
              firstActionText="Restore"
              firstActionOnClick={() => onSiloChange(TicketSilo.Tickets)}
              secondActionText="Delete forever"
              secondActionOnClick={onDeleteTicket}
              disableActions={disableSiloActions}
              isAgent={isAgent}
            />
          )}
          {shouldDisplaySpamLabel && (
            <ToolbarLabel
              title="This ticket is in Spam. You can't edit spam ticket."
              firstActionText="Not Spam"
              firstActionOnClick={() => onSiloChange(TicketSilo.Tickets)}
              secondActionText="Delete forever"
              secondActionOnClick={onDeleteTicket}
              disableActions={disableSiloActions}
              isAgent={isAgent}
            />
          )}
        </>
      )}
      {shouldDisplayChildTicketLabel && (
        <ToolbarLabel
          dataTestId="child-ticket-label"
          title={
            <>
              This ticket has a browse-only option as it was merged with ticket{" "}
              <RouterLink to={`/tickets/${parentTicketId}`} style={{ textDecoration: "none" }}>
                <Link bold={false}>{parentShortId}</Link>
              </RouterLink>
              .
            </>
          }
        />
      )}
      {shouldDisplayNotMemberOfTeamLabel && (
        <ToolbarLabel title="You're not a member of the team responsible for this ticket. You can only send private notes to other agents." />
      )}
    </Flex>
  );
};

export default TicketToolbarLabels;
